import { createStytchB2BUIClient, type StytchB2BUIConfig } from '@stytch/nextjs/b2b'
import { getStytchCookieDomain } from '@/utilities/getDomain'
import { resetAnalytics } from '@/utilities/analytics/userAnalytics'
import { getCurrentEnvironment } from '@/utilities/getEnvironment'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { clearAuthUserId } from '@/platform/http'
import { removeSelectedClientId } from '@/utilities/client/selectedClientIdStorage'
import { clearAllAnalyticsQuestionHistory } from '@/utilities/client/analyticsQuestionHistoryStorage'
import { clearAllAnalyticsProgramSelection } from '@/utilities/client/analyticsProgramSelectionStorage'
import { resetAllInMemoryFilterCommits } from '@/components/client/orders/filters/inMemoryFilterCommit'
import { getPortalType } from '@/utilities/stytch/config'
import {
  getOrganizationConfig as getPatientOrgConfig,
  type PatientOrganizationConfigOptions,
} from '@/utilities/stytch/patientPortalConfig'
import { getOrganizationConfig as getClientOrgConfig } from '@/utilities/stytch/clientPortalConfig'
import { applyPhiMaskDomToElement } from '@/components/PhiMask/phiMaskDom'
import { endImpersonationHandoff } from '@/utilities/stytch/impersonationHandoff'
import { isStytchSessionAlreadyGone } from '@/utilities/stytch/onError'
import { getExpiredStytchCookies } from '@/utilities/stytch/sessionCookies'

// Must be less than or equal to the max session duration configured in the
// Stytch dashboard (4320 minutes or 3 days as of 2025/09/17)
export const STYTCH_ACTIVE_SESSION_DURATION = getCurrentEnvironment() === 'development' ? 1440 : 60

type TextReplacementMap = Record<string, string>
type StytchMutationObserverOptions = {
  tenantName?: string
}

type LocalizeWindow = Window & {
  Localize?: {
    translatePage?: () => void
  }
}

const STYTCH_TENANT_MASK_ATTR = 'data-stytch-tenant-mask'
const STYTCH_CUSTOM_HEADER_ATTR = 'data-stytch-custom-tenant-header'
const STYTCH_ORGANIZATION_HEADER_PREFIX = 'Continue to '

/** Stytch v21 org-context header (removed in v22 UI). Kept for backwards-compatible DOM sync. */
export const STYTCH_LEGACY_ORGANIZATION_HEADER_SELECTOR = '[size="header"]'
/**
 * Stytch v22+ renders org headers with CSS-module classes (e.g. Typography_module_header__*).
 * Must stay aligned with login.css and real Stytch embed markup — see stytchClient.test.ts.
 */
export const STYTCH_V22_ORGANIZATION_HEADER_SELECTOR = '[class*="Typography_module_header"]'

const stytchOptions = {
  cookieOptions: {
    availableToSubdomains: true,
    domain: getStytchCookieDomain(),
  },
}

export const clearStytchCookies = () => {
  const cookieDomain = getStytchCookieDomain()
  const domain = cookieDomain === 'localhost' ? undefined : cookieDomain
  const cookies = document.cookie.split(';')

  cookies.forEach((cookie) => {
    const cookieParts = cookie.split('=')
    const cookieName = cookieParts[0]?.trim()

    if (cookieName?.toLowerCase().includes('stytch')) {
      for (const expired of getExpiredStytchCookies(cookieName, domain)) {
        document.cookie = expired
      }
    }
  })
}

export const getOrganizationConfig = (
  organizationSlug?: string | null,
  options?: PatientOrganizationConfigOptions,
): StytchB2BUIConfig => {
  const portalType = getPortalType()
  const baseConfig =
    portalType === 'client' ? getClientOrgConfig(options) : getPatientOrgConfig(options)

  if (!organizationSlug) {
    return baseConfig
  }

  return {
    ...baseConfig,
    organizationSlug,
  }
}

const buildMaskedTenantFragment = (
  ownerDocument: Document,
  text: string,
  tenantName: string,
): { fragment: DocumentFragment; didWrapTenantName: boolean } => {
  const fragment = ownerDocument.createDocumentFragment()
  let cursor = 0
  let didWrapTenantName = false

  while (cursor < text.length) {
    const matchIndex = text.indexOf(tenantName, cursor)
    if (matchIndex === -1) {
      fragment.appendChild(ownerDocument.createTextNode(text.slice(cursor)))
      break
    }

    if (matchIndex > cursor) {
      fragment.appendChild(ownerDocument.createTextNode(text.slice(cursor, matchIndex)))
    }

    const maskedTenantName = ownerDocument.createElement('data')
    maskedTenantName.setAttribute(STYTCH_TENANT_MASK_ATTR, 'true')
    maskedTenantName.setAttribute('ignore', '')
    maskedTenantName.textContent = tenantName
    applyPhiMaskDomToElement(maskedTenantName, { maskPosthog: false })
    fragment.appendChild(maskedTenantName)

    cursor = matchIndex + tenantName.length
    didWrapTenantName = true
  }

  return { fragment, didWrapTenantName }
}

const replaceTextInContainer = (
  stytchContainer: Element,
  replacements: TextReplacementMap,
): boolean => {
  if (!Object.keys(replacements).length) {
    return false
  }

  const walker = document.createNodeIterator(stytchContainer, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()
  let didReplaceText = false

  while (node) {
    const { nodeValue } = node
    if (!nodeValue) {
      node = walker.nextNode()
      continue
    }

    let nextValue = nodeValue

    Object.entries(replacements).forEach(([sourceText, replacementText]) => {
      nextValue = nextValue.replaceAll(sourceText, replacementText)
    })

    if (nextValue !== nodeValue) {
      node.nodeValue = nextValue
      didReplaceText = true
    }

    node = walker.nextNode()
  }

  return didReplaceText
}

const wrapTenantNameInTextNode = (textNode: Text, tenantName: string): boolean => {
  const { ownerDocument, parentNode } = textNode
  const nodeValue = textNode.nodeValue ?? ''
  if (!parentNode || !nodeValue.includes(tenantName)) {
    return false
  }

  const { fragment, didWrapTenantName } = buildMaskedTenantFragment(
    ownerDocument,
    nodeValue,
    tenantName,
  )

  if (!didWrapTenantName) {
    return false
  }

  parentNode.replaceChild(fragment, textNode)
  return true
}

const getHeaderProtectedText = (headerText: string, tenantName?: string): string | undefined => {
  const normalizedTenantName = tenantName?.trim()
  if (normalizedTenantName && headerText.includes(normalizedTenantName)) {
    return normalizedTenantName
  }

  if (
    headerText.startsWith(STYTCH_ORGANIZATION_HEADER_PREFIX) &&
    headerText !== 'Continue to ...'
  ) {
    return headerText.slice(STYTCH_ORGANIZATION_HEADER_PREFIX.length).trim() || undefined
  }

  return undefined
}

const collectStytchOrganizationHeaderElements = (stytchContainer: Element): HTMLElement[] => {
  const seen = new Set<HTMLElement>()

  for (const selector of [
    STYTCH_LEGACY_ORGANIZATION_HEADER_SELECTOR,
    STYTCH_V22_ORGANIZATION_HEADER_SELECTOR,
  ]) {
    stytchContainer.querySelectorAll<HTMLElement>(selector).forEach((element) => {
      seen.add(element)
    })
  }

  return [...seen]
}

const replaceTenantHeaderInContainer = (stytchContainer: Element, tenantName?: string): boolean => {
  let didReplaceHeader = false
  collectStytchOrganizationHeaderElements(stytchContainer).forEach((headerElement) => {
    if (headerElement.querySelector(`[${STYTCH_TENANT_MASK_ATTR}="true"]`)) {
      return
    }

    const headerText = headerElement.textContent.replace(/\s+/g, ' ').trim()
    const protectedText = getHeaderProtectedText(headerText, tenantName)
    if (!protectedText) {
      return
    }

    const { fragment, didWrapTenantName } = buildMaskedTenantFragment(
      headerElement.ownerDocument,
      headerText,
      protectedText,
    )

    if (!didWrapTenantName) {
      return
    }

    headerElement.replaceChildren(fragment)
    headerElement.setAttribute(STYTCH_CUSTOM_HEADER_ATTR, 'true')
    didReplaceHeader = true
  })

  return didReplaceHeader
}

const isolateTenantNameInContainer = (stytchContainer: Element, tenantName?: string): boolean => {
  const normalizedTenantName = tenantName?.trim()
  if (!normalizedTenantName) {
    return false
  }

  const textNodesToProcess: Text[] = []
  const walker = document.createNodeIterator(stytchContainer, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()

  while (node) {
    if (node instanceof Text) {
      const parentElement = node.parentElement
      if (
        parentElement &&
        node.nodeValue?.includes(normalizedTenantName) &&
        !parentElement.closest(`[${STYTCH_TENANT_MASK_ATTR}="true"]`) &&
        !parentElement.closest('[translate="no"]')
      ) {
        textNodesToProcess.push(node)
      }
    }

    node = walker.nextNode()
  }

  return textNodesToProcess.reduce((didWrapAny, textNode) => {
    return wrapTenantNameInTextNode(textNode, normalizedTenantName) || didWrapAny
  }, false)
}

let pendingLocalizeTranslate = false
const queueLocalizeTranslatePage = () => {
  if (typeof window === 'undefined' || pendingLocalizeTranslate) {
    return
  }

  const localize = (window as LocalizeWindow).Localize
  if (!localize?.translatePage) {
    return
  }

  pendingLocalizeTranslate = true
  const flush = () => {
    pendingLocalizeTranslate = false
    localize.translatePage?.()
  }

  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(flush)
    return
  }

  window.setTimeout(flush, 0)
}

export const syncStytchContainerContent = (
  stytchContainer: Element,
  replacements: TextReplacementMap = {},
  options: StytchMutationObserverOptions = {},
) => {
  const didReplaceText = replaceTextInContainer(stytchContainer, replacements)
  const didReplaceHeader = replaceTenantHeaderInContainer(stytchContainer, options.tenantName)
  const didIsolateTenantName = isolateTenantNameInContainer(stytchContainer, options.tenantName)

  if (didReplaceText || didReplaceHeader || didIsolateTenantName) {
    queueLocalizeTranslatePage()
  }
}

const getStytchContainer = (): Element | null => {
  if (typeof document === 'undefined') {
    return null
  }

  const stytchWrapper = document.querySelector('.stytch-wrapper')
  const stytchB2bUi = document.querySelector('stytch-b2b-ui')

  return stytchWrapper ?? stytchB2bUi
}

/** Max rAF ticks to wait for `.stytch-wrapper` / `stytch-b2b-ui` (e.g. after loading UI or Stytch inject). */
const STYTCH_CONTAINER_ATTACH_MAX_FRAMES = 90

export const createStytchMutationObserver = (
  replacements: TextReplacementMap = {},
  options: StytchMutationObserverOptions = {},
): MutationObserver | undefined => {
  const stytchElement = getStytchContainer()
  const shouldObserve = Object.keys(replacements).length > 0 || Boolean(options.tenantName?.trim())

  if (!stytchElement || !shouldObserve) {
    return
  }

  const observer = new MutationObserver(() => {
    syncStytchContainerContent(stytchElement, replacements, options)
  })

  observer.observe(stytchElement, {
    childList: true,
    characterData: true,
    subtree: true,
  })

  syncStytchContainerContent(stytchElement, replacements, options)

  return observer
}

/**
 * Attaches the Stytch copy/tenant DOM observer once the embed exists. Retries across animation frames
 * when the container is not in the document yet (e.g. initial loading spinner, or Stytch mounting after `isInitialized`).
 */
export const scheduleStytchMutationObserver = (
  replacements: TextReplacementMap = {},
  options: StytchMutationObserverOptions = {},
): (() => void) => {
  const shouldObserve = Object.keys(replacements).length > 0 || Boolean(options.tenantName?.trim())
  if (!shouldObserve) {
    return () => {}
  }

  let observer: MutationObserver | undefined
  let rafId: number | null = null
  let attempts = 0

  const tick = () => {
    rafId = null
    observer = createStytchMutationObserver(replacements, options)
    if (observer) return
    attempts += 1
    if (attempts >= STYTCH_CONTAINER_ATTACH_MAX_FRAMES) return
    rafId = requestAnimationFrame(tick)
  }

  tick()

  return () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
    }
    observer?.disconnect()
  }
}

export const stytchClient = createStytchB2BUIClient(
  process.env.NEXT_PUBLIC_STYTCH_PUBLIC_TOKEN || '',
  stytchOptions,
)
export const logout = async () => {
  // Clear PHI filters before revocation. The logout page still navigates to
  // login when revoke fails, and a later login in this tab must not reuse them.
  resetAllInMemoryFilterCommits()
  try {
    await stytchClient.session.revoke()
  } catch (error) {
    // No session is the logout end state (expired, already revoked, or never established).
    if (!isStytchSessionAlreadyGone(error)) {
      throw error
    }
  }
  clearStytchCookies()
}

/**
 * Unified logout function that handles both Stytch logout and analytics reset
 */
export const logoutWithAnalytics = async (properties?: { reason?: string; source?: string }) => {
  trackEvent('auth:logout', {
    reason: properties?.reason,
    source: properties?.source,
  })

  resetAnalytics()

  // Clear auth user ID from localStorage to prevent stale IDs in subsequent requests
  clearAuthUserId()

  // Clear selected client so pre-auth pages do not reuse the previous session's partner
  removeSelectedClientId()

  // Ask Your Data history is free-text and must not outlive the session
  clearAllAnalyticsQuestionHistory()
  // Program selection is member-scoped, but leftover keys must not outlive the session
  clearAllAnalyticsProgramSelection()

  // Module-level impersonation handoff survives client-side logout navigation.
  // Reset it here so a later login in the same JS session can detect expiry.
  endImpersonationHandoff()

  // Use Stytch client SDK to properly revoke session and clear cookies
  await logout()
}
